import React, { useState, useEffect, useCallback } from 'react';
import { sourcesApi, integrationsApi, outletsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  Upload,
  Plugs,
  ArrowsClockwise,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Lightning,
  CaretRight
} from '@phosphor-icons/react';

const SOURCE_TYPES = [
  { value: 'NATIVE_1OS_POS', label: '1OS Native POS' },
  { value: 'EXTERNAL_POS', label: 'External POS' },
  { value: 'TRANSACTION_UPLOAD', label: 'Transaction Upload' },
  { value: 'ITEM_SUMMARY_UPLOAD', label: 'Item Summary Upload' },
  { value: 'MARKETPLACE_IMPORT', label: 'Marketplace Import' },
  { value: 'MANUAL_BACKFILL', label: 'Manual Backfill' }
];

const AUTH_TYPES = [
  { value: 'API_KEY', label: 'API Key' },
  { value: 'BEARER', label: 'Bearer Token' },
  { value: 'BASIC', label: 'Basic Auth' },
  { value: 'OAUTH2', label: 'OAuth 2.0' },
  { value: 'NONE', label: 'None' }
];

const IntegrationsPage = () => {
  const [sources, setSources] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSource, setShowAddSource] = useState(false);
  const [showUploadDocs, setShowUploadDocs] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [blueprint, setBlueprint] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const [newSource, setNewSource] = useState({
    source_type: 'EXTERNAL_POS',
    source_name: '',
    display_name: '',
    outlet_id: '',
    auth_type: 'API_KEY',
    base_url: '',
    polling_enabled: false,
    polling_frequency_minutes: 15,
    source_timezone: 'Asia/Kolkata',
    business_day_cutoff_hour: 5
  });

  const fetchSources = useCallback(async () => {
    try {
      const [sourcesRes, outletsRes] = await Promise.all([
        sourcesApi.list(),
        outletsApi.list()
      ]);
      setSources(sourcesRes.data);
      setOutlets(outletsRes.data);
    } catch (err) {
      toast.error('Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  const handleCreateSource = async () => {
    try {
      const res = await sourcesApi.create(newSource);
      toast.success('Source created successfully');
      setShowAddSource(false);
      setNewSource({
        source_type: 'EXTERNAL_POS',
        source_name: '',
        display_name: '',
        outlet_id: '',
        auth_type: 'API_KEY',
        base_url: '',
        polling_enabled: false,
        polling_frequency_minutes: 15,
        source_timezone: 'Asia/Kolkata',
        business_day_cutoff_hour: 5
      });
      fetchSources();
    } catch (err) {
      toast.error('Failed to create source');
    }
  };

  const handleUploadDocs = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAnalyzing(true);
    try {
      const uploadRes = await integrationsApi.uploadDocument(file, selectedSource?.id);
      const docId = uploadRes.data.integration_document_id;

      toast.info('Analyzing document...');
      const analyzeRes = await integrationsApi.analyzeDocument(docId);
      setBlueprint(analyzeRes.data);
      toast.success('Analysis complete');
    } catch (err) {
      toast.error('Document analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSync = async (sourceId) => {
    try {
      await sourcesApi.sync(sourceId);
      toast.success('Sync triggered');
      fetchSources();
    } catch (err) {
      toast.error('Sync failed');
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      CONNECTED: 'status-connected',
      SYNCING: 'status-syncing',
      DEGRADED: 'status-degraded',
      FAILED: 'status-failed',
      DRAFT: 'status-draft',
      DISCONNECTED: 'status-failed'
    };
    return `status-badge ${classes[status] || 'status-draft'}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Loading integrations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-page">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h1 className="text-2xl font-medium font-['Outfit'] tracking-tight">Integrations</h1>
          <p className="text-sm text-zinc-400 mt-1">Connect and manage your sales sources</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={showUploadDocs} onOpenChange={setShowUploadDocs}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                data-testid="upload-api-docs-btn"
              >
                <Upload size={18} className="mr-2" />
                Upload API Docs
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121214] border-white/10 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">Analyze API Documentation</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-sm text-zinc-400">
                  Upload API docs (PDF, OpenAPI, Postman, JSON) to auto-detect integration requirements.
                </p>
                <div className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center">
                  <input
                    type="file"
                    accept=".pdf,.json,.yaml,.yml,.txt,.md"
                    onChange={handleUploadDocs}
                    className="hidden"
                    id="doc-upload"
                    data-testid="doc-upload-input"
                  />
                  <label 
                    htmlFor="doc-upload"
                    className="cursor-pointer"
                  >
                    <FileText size={48} className="mx-auto text-blue-400 mb-4" />
                    <p className="text-sm text-zinc-300">
                      {analyzing ? 'Analyzing...' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      PDF, OpenAPI, Postman, JSON, Markdown
                    </p>
                  </label>
                </div>

                {blueprint && (
                  <div className="surface-base rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-white">Integration Blueprint</h3>
                      <span className="text-xs font-mono text-emerald-400">
                        {(blueprint.confidence_score * 100).toFixed(0)}% confidence
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-zinc-400">Suggested Source</p>
                        <p className="text-white font-medium">{blueprint.suggested_source_name || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400">Auth Strategy</p>
                        <p className="text-white font-medium">{blueprint.auth_strategy || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400">Ingestion Mode</p>
                        <p className="text-white font-medium">{blueprint.recommended_ingestion_mode || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400">Required Mappings</p>
                        <p className="text-white font-medium">{blueprint.required_mappings?.length || 0} types</p>
                      </div>
                    </div>

                    {blueprint.detected_endpoints?.length > 0 && (
                      <div>
                        <p className="text-zinc-400 text-sm mb-2">Detected Endpoints</p>
                        <div className="space-y-1">
                          {blueprint.detected_endpoints.slice(0, 5).map((ep) => (
                            <div key={`${ep.method}-${ep.path}`} className="flex items-center gap-2 text-xs font-mono bg-zinc-800/50 px-2 py-1 rounded">
                              <span className="text-cyan-400">{ep.method || 'GET'}</span>
                              <span className="text-zinc-300">{ep.path}</span>
                              <span className="text-zinc-500">→ {ep.entity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {blueprint.next_steps_markdown && (
                      <div>
                        <p className="text-zinc-400 text-sm mb-2">Next Steps</p>
                        <div className="text-sm text-zinc-300 whitespace-pre-wrap bg-zinc-800/50 p-3 rounded">
                          {blueprint.next_steps_markdown}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showAddSource} onOpenChange={setShowAddSource}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="add-source-btn"
              >
                <Plus size={18} className="mr-2" />
                Connect POS
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121214] border-white/10 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">Add Sales Source</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Source Type</Label>
                  <Select
                    value={newSource.source_type}
                    onValueChange={(v) => setNewSource({ ...newSource, source_type: v })}
                  >
                    <SelectTrigger data-testid="source-type-select" className="bg-[#18181B] border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Source Name</Label>
                    <Input
                      placeholder="petpooja"
                      value={newSource.source_name}
                      onChange={(e) => setNewSource({ ...newSource, source_name: e.target.value })}
                      data-testid="source-name-input"
                      className="bg-[#18181B] border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Display Name</Label>
                    <Input
                      placeholder="Petpooja - BENO Goa"
                      value={newSource.display_name}
                      onChange={(e) => setNewSource({ ...newSource, display_name: e.target.value })}
                      data-testid="display-name-input"
                      className="bg-[#18181B] border-white/10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Outlet</Label>
                  <Select
                    value={newSource.outlet_id}
                    onValueChange={(v) => setNewSource({ ...newSource, outlet_id: v })}
                  >
                    <SelectTrigger data-testid="outlet-select" className="bg-[#18181B] border-white/10">
                      <SelectValue placeholder="Select outlet" />
                    </SelectTrigger>
                    <SelectContent>
                      {outlets.map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Auth Type</Label>
                    <Select
                      value={newSource.auth_type}
                      onValueChange={(v) => setNewSource({ ...newSource, auth_type: v })}
                    >
                      <SelectTrigger className="bg-[#18181B] border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AUTH_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Base URL</Label>
                    <Input
                      placeholder="https://api.pos.com"
                      value={newSource.base_url}
                      onChange={(e) => setNewSource({ ...newSource, base_url: e.target.value })}
                      className="bg-[#18181B] border-white/10"
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleCreateSource}
                  className="w-full bg-blue-500 hover:bg-blue-600"
                  data-testid="create-source-btn"
                >
                  Create Source
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Sources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sources.map((source) => (
          <div 
            key={source.id}
            className="surface-base rounded-lg p-5 card-hover cursor-pointer"
            onClick={() => setSelectedSource(source)}
            data-testid={`source-card-${source.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  source.status === 'CONNECTED' ? 'bg-emerald-500/10' :
                  source.status === 'DEGRADED' ? 'bg-amber-500/10' :
                  source.status === 'SYNCING' ? 'bg-blue-500/10' :
                  'bg-zinc-500/10'
                }`}>
                  <Plugs size={24} weight="duotone" className={
                    source.status === 'CONNECTED' ? 'text-emerald-400' :
                    source.status === 'DEGRADED' ? 'text-amber-400' :
                    source.status === 'SYNCING' ? 'text-blue-400' :
                    'text-zinc-400'
                  } />
                </div>
                <div>
                  <p className="font-medium">{source.display_name}</p>
                  <p className="text-xs text-zinc-500">{source.source_type}</p>
                </div>
              </div>
              <span className={getStatusBadge(source.status)}>
                {source.status}
              </span>
            </div>

            <div className="space-y-2 text-sm">
              {source.last_successful_sync_at && (
                <div className="flex items-center gap-2 text-zinc-400">
                  <CheckCircle size={14} className="text-emerald-400" />
                  <span>Last sync: {new Date(source.last_successful_sync_at).toLocaleString()}</span>
                </div>
              )}
              {source.last_error_message && (
                <div className="flex items-center gap-2 text-zinc-400">
                  <XCircle size={14} className="text-red-400" />
                  <span className="truncate">{source.last_error_message}</span>
                </div>
              )}
              {source.polling_enabled && (
                <div className="flex items-center gap-2 text-zinc-400">
                  <Clock size={14} />
                  <span>Polling every {source.polling_frequency_minutes} min</span>
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-white/5 flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); handleSync(source.id); }}
                className="text-xs"
                data-testid={`sync-btn-${source.id}`}
              >
                <ArrowsClockwise size={14} className="mr-1" />
                Sync
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-xs"
              >
                <Lightning size={14} className="mr-1" />
                Configure
              </Button>
            </div>
          </div>
        ))}

        {sources.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Plugs size={48} className="mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">No sources configured yet</p>
            <p className="text-sm text-zinc-500 mt-1">Add a POS integration to start capturing sales</p>
          </div>
        )}
      </div>

      {/* Source Details Drawer */}
      {selectedSource && (
        <div className="fixed inset-y-0 right-0 w-full max-w-md bg-[#121214] border-l border-white/10 z-50 animate-slide-in-right">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="font-medium">{selectedSource.display_name}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedSource(null)}
                data-testid="close-drawer-btn"
              >
                ×
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="surface-base rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Status</span>
                  <span className={getStatusBadge(selectedSource.status)}>{selectedSource.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Type</span>
                  <span>{selectedSource.source_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Auth</span>
                  <span>{selectedSource.auth_type || 'None'}</span>
                </div>
                {selectedSource.base_url && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Base URL</span>
                    <span className="text-xs font-mono truncate max-w-[200px]">{selectedSource.base_url}</span>
                  </div>
                )}
              </div>

              <div className="surface-base rounded-lg p-4">
                <h4 className="text-sm font-medium mb-3">Sync Configuration</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Polling</span>
                    <span>{selectedSource.polling_enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  {selectedSource.polling_enabled && (
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Frequency</span>
                      <span>{selectedSource.polling_frequency_minutes} minutes</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Timezone</span>
                    <span>{selectedSource.source_timezone || 'Asia/Kolkata'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Day Cutoff</span>
                    <span>{selectedSource.business_day_cutoff_hour || 5}:00 AM</span>
                  </div>
                </div>
              </div>

              <div className="surface-base rounded-lg p-4">
                <h4 className="text-sm font-medium mb-3">Capabilities</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedSource.supports_orders && <span className="status-badge status-connected">Orders</span>}
                  {selectedSource.supports_payments && <span className="status-badge status-connected">Payments</span>}
                  {selectedSource.supports_settlements && <span className="status-badge status-connected">Settlements</span>}
                  {selectedSource.supports_refunds && <span className="status-badge status-connected">Refunds</span>}
                  {selectedSource.supports_voids && <span className="status-badge status-connected">Voids</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsPage;
